import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

/** Every expandable dashboard card. The value lives in `?view=` so it deep-links. */
export type DashboardView =
  | 'kpi-staff'
  | 'kpi-present'
  | 'kpi-late'
  | 'kpi-leave'
  | 'kpi-absent'
  | 'kpi-clocked'
  | 'kpi-payroll'
  | 'overview'
  | 'trends'
  | 'clock-ins'
  | 'employees'
  | 'attention'
  | 'mix'
  | 'coverage'
  | 'watch'

const VIEW_PARAM = 'view'

interface ViewHistoryState {
  dashboardView?: true
}

/**
 * The open detail modal, kept in the URL. Opening pushes a history entry so
 * the browser Back button closes it; closing pops that entry when we pushed
 * it, and otherwise (a pasted deep link) just drops the param.
 */
export function useDashboardView(): {
  view: DashboardView | null
  openView: (view: DashboardView) => void
  closeView: () => void
} {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const view = (searchParams.get(VIEW_PARAM) as DashboardView | null) ?? null
  const pushedByUs = (location.state as ViewHistoryState | null)?.dashboardView === true

  // openView/closeView keep one identity for the page's lifetime. Modal's
  // scroll-lock effect depends on onClose; a new callback every render made
  // it unlock and relock the page (a visible flicker) on each re-render.
  const latest = useRef({ searchParams, setSearchParams, navigate, pushedByUs })
  useEffect(() => {
    latest.current = { searchParams, setSearchParams, navigate, pushedByUs }
  })

  const openView = useCallback((next: DashboardView) => {
    const params = new URLSearchParams(latest.current.searchParams)
    params.set(VIEW_PARAM, next)
    latest.current.setSearchParams(params, {
      state: { dashboardView: true } satisfies ViewHistoryState,
    })
  }, [])

  const closeView = useCallback(() => {
    if (latest.current.pushedByUs) {
      latest.current.navigate(-1)
      return
    }
    const params = new URLSearchParams(latest.current.searchParams)
    params.delete(VIEW_PARAM)
    latest.current.setSearchParams(params, { replace: true })
  }, [])

  return { view, openView, closeView }
}
